import { ManageActionPanel } from "@/overlay/components/LayerStackWindow/ManageActionPanel/ManageActionPanel";
import { classNames } from "@/utility/jsx";
import { type PropsWithChildren, type ReactNode, useState } from "react";

export const TEST_IDS = {
  sectionHeader: "Section_section-header",
  panelContent: "Section_panel-content",
};

interface HeaderProps {
  seriesType?: string;
  color?: string;
  expanded: boolean;
  hideTag?: boolean;
  tagStyle?: TagStyle;
  actionButton?: ReactNode;
  onExpanedChange: (expanded: boolean) => void;
}

function Header({
  seriesType,
  color,
  expanded,
  hideTag,
  tagStyle,
  actionButton,
  onExpanedChange,
  children,
}: PropsWithChildren<HeaderProps>) {
  const toggle = () => onExpanedChange(!expanded);

  return (
    <div className={"expansion-panel__header"} data-testid={TEST_IDS.sectionHeader}>
      <div
        className={classNames(
          "expansion-panel__header__layer-info",
          hideTag ? "expansion-panel__header__layer-info__sub" : "",
        )}
        onClick={toggle}
      >
        <span className="expansion-panel__chevron">
          <span className="material-icons">{expanded ? "expand_less" : "expand_more"}</span>
        </span>
        <span className="layer-label">
          {!hideTag && (
            <span className={classNames("layer-label__kindName", "tag", tagStyle)}>
              {seriesType}
              {color ? <span className="tag-param-color" style={{ backgroundColor: color }} /> : null}
            </span>
          )}
          <span className="layer-label__kindName">{children}</span>
        </span>
      </div>
      {actionButton && <div className="expansion-panel__header__right-control">{actionButton}</div>}
    </div>
  );
}

export enum TagStyle {
  PRIMARY = "",
  SECONDARY = "secondary",
  TERTIARY = "tertiary",
}

interface SectionProps {
  tag?: string;
  color?: string;
  title?: string;
  tagStyle?: TagStyle;
  subSection?: boolean;
  defaultOpen?: boolean;
  onRemove?: () => void;
  onHide?: () => void;
  isVisible?: boolean;
}

export function Section({
  tag,
  color,
  title,
  subSection,
  defaultOpen = false,
  tagStyle = TagStyle.PRIMARY,
  children,
  onRemove,
  onHide,
  isVisible = true,
}: PropsWithChildren<SectionProps>) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const actionButton =
    onHide || onRemove ? <ManageActionPanel onRemove={onRemove} onHide={onHide} isVisible={isVisible} /> : undefined;

  return (
    <>
      <div
        className={classNames(
          "expansion-panel box-section--breakout actionable",
          isOpen && "expansion-panel--expanded",
        )}
      >
        <Header
          seriesType={tag}
          color={color}
          expanded={isOpen}
          hideTag={subSection}
          tagStyle={tagStyle}
          actionButton={actionButton}
          onExpanedChange={(expanded) => setIsOpen(expanded)}
        >
          {title}
        </Header>
      </div>
      {isOpen && (
        <div className="expansion-panel__content" data-testid={TEST_IDS.panelContent}>
          {children}
        </div>
      )}
    </>
  );
}
